<template>
    <div class="my-2 rounded-[12.8px] border border-gray-200 bg-white p-4">
        <h4 v-if="spec.title" class="mb-3 text-sm font-medium text-gray-800">
            {{ spec.title }}
        </h4>
        <div class="h-56">
            <canvas ref="canvas"></canvas>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { onBeforeUnmount, onMounted, ref, watch } from "vue";
import Chart from "chart.js/auto";
import type { ChartConfiguration } from "chart.js";
import type { AiChartSpec } from "@/Pages/AiChat/markdown";

const props = defineProps<{
    spec: AiChartSpec;
}>();

const PALETTE = [
    "#3B82F6",
    "#10B981",
    "#EF4444",
    "#F59E0B",
    "#8B5CF6",
    "#14B8A6",
];

// Semantic colours matching the dashboard/reports: total = blue, pass/compliant
// = green, fail/non-compliant = red. Falls back to the palette by index.
const SEMANTIC = { blue: "#3B82F6", green: "#10B981", red: "#EF4444" };

const colorFor = (name: string, index: number): string => {
    const value = (name ?? "").toLowerCase();

    if (
        /non-?compliant|noncompliant|nekompl|neúsp|invalid|fail|reject|blacklist/.test(
            value,
        )
    ) {
        return SEMANTIC.red;
    }
    if (/compliant|valid|pass|shod|úsp|deliver|success/.test(value)) {
        return SEMANTIC.green;
    }
    if (/total|celkem|reported|sum|volume|emails/.test(value)) {
        return SEMANTIC.blue;
    }

    return PALETTE[index % PALETTE.length];
};

const canvas = ref<HTMLCanvasElement | null>(null);
let chart: Chart | null = null;

const buildConfig = (): ChartConfiguration => {
    const isDoughnut = props.spec.type === "doughnut";
    const isBar = props.spec.type === "bar";
    const singleSeries = props.spec.series.length === 1;
    // A single bar/doughnut series is coloured per bar/slice by its label.
    const perLabel = isDoughnut || (isBar && singleSeries);

    const datasets = props.spec.series.map((series, index) => {
        const labelColors = props.spec.labels.map((label, i) =>
            colorFor(label, i),
        );
        const seriesColor = colorFor(series.name, index);

        if (isDoughnut) {
            return {
                label: series.name,
                data: series.data,
                backgroundColor: labelColors,
                borderWidth: 0,
            };
        }

        const background = perLabel ? labelColors : seriesColor;

        return {
            label: series.name,
            data: series.data,
            backgroundColor: background,
            borderColor: isBar ? background : seriesColor,
            borderWidth: isBar ? 0 : 2,
            tension: 0.3,
            fill: false,
        };
    });

    return {
        type: props.spec.type,
        data: { labels: props.spec.labels, datasets },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: props.spec.series.length > 1 || isDoughnut,
                    labels: { boxWidth: 12, font: { size: 11 } },
                },
            },
            scales: isDoughnut
                ? {}
                : {
                      x: { ticks: { font: { size: 11 } } },
                      y: { beginAtZero: true, ticks: { font: { size: 11 } } },
                  },
        },
    };
};

const render = (): void => {
    if (canvas.value === null) {
        return;
    }
    chart?.destroy();
    chart = new Chart(canvas.value, buildConfig());
};

onMounted(render);
watch(() => props.spec, render, { deep: true });
onBeforeUnmount(() => chart?.destroy());
</script>
